// src/services/api.js
import axios from 'axios';
import { announceApproval } from './approvalBridge';

const API = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A 401 means the token itself is missing/invalid/expired — the session is
// dead, so clear it and send the user back to log in. A 403 means the token
// is fine but the role lacks permission for that action, which is a normal
// in-app error, not a logged-out state — so it's deliberately left alone here.
API.interceptors.response.use(
  (response) => {
    // A sensitive action (voiding, a credit/debit note, a delete...) by a sales user isn't done: it is asked of the admins
    // (HTTP 202 { approvalRequested }) and the user carries on. Say so, wherever in the app it happened.
    if (response.status === 202 && response.data?.approvalRequested) announceApproval(response.data);
    return response;
  },
  async (error) => {
    // Emergency lockdown: only owners are let in. Anyone else is signed out and shown why on the login page.
    if (error.response?.status === 503 && error.response.data?.code === 'LOCKDOWN' && !String(error.config?.url || '').includes('/login')) {
      ['token', 'userName', 'userRole', 'userIsOwner', 'mustChangePassword'].forEach(k => localStorage.removeItem(k));
      sessionStorage.setItem('ventas_lockdown', '1');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }

    // An admin reset this account's password (or created it): nothing works
    // until the person chooses their own.
    if (error.response?.status === 403 && error.response.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
      localStorage.setItem('mustChangePassword', '1');
      if (window.location.pathname !== '/change-password') window.location.href = '/change-password';
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('userName');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userIsOwner');
      localStorage.removeItem('mustChangePassword');
      if (window.location.pathname !== '/login') {
        sessionStorage.setItem('ventas_session_expired', '1');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default API;
